import { Link } from "react-router-dom";
import { ArrowRight, Mountain, Factory, Ruler, PackageCheck } from "lucide-react";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { IMAGES } from "../data/siteData";

const capabilities = [
  {
    icon: Mountain,
    title: "Captive Quarries & Mines",
    text: "Our own extraction sites for granite, marble and dolomite give us first pick of every block and a steady, traceable supply.",
  },
  {
    icon: Factory,
    title: "Processing Facility",
    text: "In-house cutting and finishing lines convert rough blocks into slabs, tiles and cut-to-size pieces with uniform calibration.",
  },
  {
    icon: Ruler,
    title: "Grading & Quality Checks",
    text: "Every lot is inspected for colour match, finish and dimensional accuracy before it is cleared for dispatch.",
  },
  {
    icon: PackageCheck,
    title: "Packing & Dispatch",
    text: "Export-grade crating and container loading, with documentation handled for both domestic and international shipments.",
  },
];

const Infrastructure = () => (
  <main data-testid="infrastructure-page">
    <section className="bg-[#F8F9FA] py-20 md:py-28 border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Infrastructure</Eyebrow>
          <h1 className="mt-4 max-w-3xl text-5xl md:text-6xl font-bold tracking-tighter" data-testid="infra-heading">
            Built to handle stone at scale
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-gray-600" data-testid="infra-intro">
            A complete chain — quarry, processing, grading and dispatch — means the quality we show you in a sample
            is the quality that reaches your site or container.
          </p>
        </Reveal>
      </div>
    </section>

    {/* Asymmetric feature */}
    <section className="py-20 md:py-24 bg-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8 items-center">
        <Reveal className="lg:col-span-8">
          <img
            src={IMAGES.factory}
            alt="Stone cutting and processing machinery at our facility"
            className="h-[460px] w-full border border-gray-200 object-cover"
            data-testid="infra-factory-image"
          />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-4">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="infra-feature-heading">
            Precision cutting, honest output
          </h2>
          <p className="mt-6 text-base leading-relaxed text-gray-600">
            Our processing unit runs gangsaw and cutting lines that turn raw blocks into calibrated slabs and tiles.
            Because processing sits beside our own quarry supply, there is no middle step where quality can slip.
          </p>
          <p className="mt-4 text-base leading-relaxed text-gray-600">
            Visitors are welcome at our facility — see the yard, the lines and the stock before you place an order.
          </p>
        </Reveal>
      </div>
    </section>

    {/* Capabilities grid */}
    <section className="py-20 md:py-24 bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Capabilities</Eyebrow>
          <h2 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight">From rock face to loading bay</h2>
        </Reveal>
        <div className="mt-14 grid gap-8 sm:grid-cols-2">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div
                className="flex h-full gap-6 border border-gray-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-black"
                data-testid={`capability-card-${i}`}
              >
                <c.icon size={30} className="shrink-0 text-[#D40000]" strokeWidth={1.5} />
                <div>
                  <h3 className="text-xl font-medium">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Quarry strip */}
    <section className="relative py-32 md:py-44 overflow-hidden" data-testid="infra-quarry-strip">
      <img src={IMAGES.quarry} alt="Quarry landscape" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 mx-auto max-w-4xl px-4 text-center text-white">
        <Reveal>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
            Where every order begins — our own ground
          </h2>
          <Link
            to="/contact"
            data-testid="infra-cta-button"
            className="mt-10 inline-flex items-center gap-2 bg-[#D40000] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-red-900"
          >
            Plan a Visit or Enquiry <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Infrastructure;
